/**
 * Utility functions for generating and hashing IDs
 */

/**
 * Hash a string using SHA-256 and return first N characters
 * @param {string} input - String to hash
 * @param {number} length - Number of characters to return (default: 12)
 * @returns {Promise<string>} Hashed string
 */
export async function hashString(input, length = 12) {
  if (!input) {
    throw new Error('Input string is required')
  }
  
  // Use Web Crypto API (available in both browser and Node.js)
  const encoder = new TextEncoder()
  const data = encoder.encode(input)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  
  // Return first N characters
  return hashHex.substring(0, length)
}

/**
 * Strip region prefix from Cognito Identity Pool identity ID
 * Example: "us-east-2:e7e5fad4-8efc-c900-7b96-818b14202ac9" -> "e7e5fad4-8efc-c900-7b96-818b14202ac9"
 * @param {string} identityId - Full identity ID with region prefix
 * @returns {string} Identity ID without region prefix
 */
export function stripRegionPrefix(identityId) {
  if (!identityId) {
    return identityId
  }
  
  // Remove region prefix (format: "region:uuid")
  const colonIndex = identityId.indexOf(':')
  if (colonIndex > 0) {
    return identityId.substring(colonIndex + 1)
  }
  
  return identityId
}

/**
 * Get hashed user ID for S3 path
 * Strips region prefix and hashes to 12 characters
 * @param {string} identityId - Full Cognito Identity Pool identity ID
 * @returns {Promise<string>} Hashed user ID (12 characters)
 */
export async function getHashedUserId(identityId) {
  const withoutRegion = stripRegionPrefix(identityId)
  return await hashString(withoutRegion, 12)
}

